'use client'

import { Button } from '@chain-zen/ui'

interface IELDConfirmationModalProps {
  confirmingCurrentValue?: string | null
  confirmingLabel: string
  isLoadingUpdate: boolean
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
  pendingValue?: string | null
  type: 'replace' | 'remove'
}

const getConfirmButtonLabel = (isLoadingUpdate: boolean, isReplace: boolean): string => {
  if (isLoadingUpdate) return isReplace ? 'Replacing...' : 'Removing...'
  return isReplace ? 'Yes, Replace' : 'Yes, Remove'
}

/**
 * Inline confirmation panel for replace/remove actions in ELD asset selection.
 * Renders within the content flow (not as an overlay dialog).
 */
export const ELDConfirmationModal = ({
  confirmingCurrentValue,
  confirmingLabel,
  isLoadingUpdate,
  isOpen,
  onCancel,
  onConfirm,
  pendingValue,
  type
}: IELDConfirmationModalProps) => {
  if (!isOpen) return null

  const isReplace = type === 'replace'
  const containerClassName = isReplace
    ? 'p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg'
    : 'p-3 bg-red-500/10 border border-red-500/30 rounded-lg'
  const textClassName = isReplace ? 'text-sm text-amber-400 mb-2.5' : 'text-sm text-red-400 mb-2.5'

  return (
    <div className={containerClassName}>
      <p className={textClassName}>
        {isReplace ? (
          <>
            Replace {confirmingLabel} <span className="font-medium">{confirmingCurrentValue}</span> with{' '}
            <span className="font-medium">{pendingValue}</span>?
          </>
        ) : (
          <>
            Remove {confirmingLabel} <span className="font-medium">{confirmingCurrentValue}</span>?
          </>
        )}
      </p>
      <div className="flex gap-2">
        <Button onClick={onCancel} variant="secondary" className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoadingUpdate}
          variant={isReplace ? 'default' : 'destructive'}
          className="flex-1"
        >
          {getConfirmButtonLabel(isLoadingUpdate, isReplace)}
        </Button>
      </div>
    </div>
  )
}
